import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useI18n, BRAND, LANGUAGES } from "@/i18n";
import { format, parseISO } from "date-fns";
import { it as itLocale, enUS, fr as frLocale, es as esLocale, de as deLocale, ja as jaLocale } from "date-fns/locale";
import AvailabilityTab from "@/components/admin/AvailabilityTab";
import BookingsCalendar from "@/components/admin/BookingsCalendar";

const LOCALES = { it: itLocale, en: enUS, fr: frLocale, es: esLocale, de: deLocale, ja: jaLocale };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "terrazza_admin_token";

export default function Admin() {
  const { t, lang, setLang } = useI18n();
  const locale = LOCALES[lang] || itLocale;
  const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");

  // Prevent search engines from indexing the admin page
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Admin · Eternal City View";
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);
  const [password, setPassword] = useState("");
  const [view, setView] = useState("bookings"); // bookings | reviews | gallery | availability
  const [bookingViewMode, setBookingViewMode] = useState("list"); // list | calendar
  const [showAddReview, setShowAddReview] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("all");

  const authed = !!token;

  const handleAuthError = useCallback((err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(STORAGE_KEY);
      setToken("");
      toast.error(t.admin.wrongPassword);
    }
  }, [t.admin.wrongPassword]);

  const fetchBookings = useCallback(async (tk = token) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookings`, { headers: { "X-Admin-Password": tk } });
      setBookings(res.data);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  }, [token, handleAuthError]);

  const fetchReviews = useCallback(async (tk = token) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reviews`, { headers: { "X-Admin-Password": tk } });
      setReviews(res.data);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  }, [token, handleAuthError]);

  useEffect(() => {
    if (!authed) return;
    if (view === "bookings") {
      fetchBookings();
      setTab("all");
    } else {
      fetchReviews();
      setTab("all");
    }
  }, [authed, view, fetchBookings, fetchReviews]);

  const onLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const tk = res.data.token;
      localStorage.setItem(STORAGE_KEY, tk);
      setToken(tk);
      setPassword("");
    } catch {
      toast.error(t.admin.wrongPassword);
    }
  };

  const onLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setBookings([]);
    setReviews([]);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/bookings/${id}/status`,
        { status },
        { headers: { "X-Admin-Password": token } }
      );
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("OK");
    } catch {
      toast.error("Errore");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/bookings/${id}`, { headers: { "X-Admin-Password": token } });
      setBookings((prev) => prev.filter((b) => b.id !== id));
    } catch {
      toast.error("Errore");
    }
  };

  const updateReviewStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/reviews/${id}/status`,
        { status },
        { headers: { "X-Admin-Password": token } }
      );
      setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("OK");
    } catch {
      toast.error("Errore");
    }
  };

  const removeReview = async (id) => {
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers: { "X-Admin-Password": token } });
      setReviews((prev) => prev.filter((r) => r.id !== id));
    } catch {
      toast.error("Errore");
    }
  };

  const filtered = view === "bookings"
    ? bookings.filter((b) => (tab === "all" ? true : b.status === tab))
    : reviews.filter((r) => (tab === "all" ? true : r.status === tab));

  const statusBadge = (status) => {
    const map = {
      pending: "bg-[#C85A3C]/15 text-[#C85A3C] border-[#C85A3C]/30",
      confirmed: "bg-[#6B7A3F]/15 text-[#6B7A3F] border-[#6B7A3F]/30",
      approved: "bg-[#6B7A3F]/15 text-[#6B7A3F] border-[#6B7A3F]/30",
      cancelled: "bg-[#5C564F]/15 text-[#5C564F] border-[#5C564F]/30",
      rejected: "bg-[#5C564F]/15 text-[#5C564F] border-[#5C564F]/30",
      abandoned: "bg-[#E8A33D]/20 text-[#E8A33D] border-[#E8A33D]/60 font-semibold",
      expired: "bg-[#5C564F]/15 text-[#5C564F] border-[#5C564F]/30",
      pending_payment: "bg-[#B89668]/20 text-[#B89668] border-[#B89668]/40",
      failed: "bg-[#5C564F]/15 text-[#5C564F] border-[#5C564F]/30",
    };
    return `inline-block px-2 py-1 text-[10px] tracking-widest uppercase border rounded-sm ${map[status] || ''}`;
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#2C2520] text-[#FAF9F6] flex items-center justify-center grain relative">
        <div className="absolute top-6 left-6">
          <Link to="/" className="text-xs tracking-widest uppercase text-[#FAF9F6]/60 hover:text-[#C85A3C]" data-testid="admin-back-home">
            ← Home
          </Link>
        </div>
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md px-8"
        >
          <p className="overline text-[#C85A3C]">{t.admin.title}</p>
          <h1 className="font-serif-display text-5xl mt-4">{t.admin.loginTitle}</h1>
          <div className="mt-10">
            <label className="overline block mb-2">{t.admin.password}</label>
            <input
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-[#FAF9F6]/30 focus:border-[#C85A3C] outline-none py-3 text-[#FAF9F6]"
              autoFocus
              required
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-btn"
            className="btn-terracotta mt-10 px-8 py-4 rounded-sm tracking-[0.2em] uppercase text-xs w-full"
          >
            {t.admin.login}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#2C2520] text-[#FAF9F6]" data-testid="admin-dashboard">
      <header className="border-b border-[#FAF9F6]/10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/" className="font-serif-display text-2xl">{BRAND.name}</Link>
            <span className="overline text-[#C85A3C]">{t.admin.title}</span>
          </div>
          <div className="flex items-center gap-3 sm:gap-6">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="admin-lang-trigger"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-[#FAF9F6]/25 text-[#FAF9F6] hover:border-[#C85A3C] transition text-xs tracking-widest uppercase"
                >
                  <span className="text-base leading-none" aria-hidden="true">{currentLang.flag}</span>
                  <span className="font-semibold">{currentLang.code.toUpperCase()}</span>
                  <ChevronDown className="w-3 h-3 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#FAF9F6] border border-[#E5E0D8] rounded-sm min-w-[160px] p-1 shadow-xl">
                {LANGUAGES.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    data-testid={`lang-option-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={`flex items-center gap-3 cursor-pointer rounded-sm px-3 py-2.5 text-sm transition ${
                      lang === l.code ? "bg-[#C85A3C]/10 text-[#C85A3C] font-semibold" : "text-[#2C2520] hover:bg-[#F2EFEB]"
                    }`}
                  >
                    <span className="text-lg leading-none" aria-hidden="true">{l.flag}</span>
                    <span className="flex-1">{l.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="text-xs tracking-widest uppercase border border-[#FAF9F6]/30 px-4 py-2 rounded-sm hover:bg-[#FAF9F6] hover:text-[#2C2520] transition"
            >
              {t.admin.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
        {/* View switch: Bookings | Reviews */}
        <div className="flex items-center gap-2 mb-10 border-b border-[#FAF9F6]/10 pb-1">
          {[
            { v: "bookings", label: t.admin.bookings, count: bookings.length },
            { v: "reviews", label: t.admin.reviewsTitle, count: reviews.length, pending: reviews.filter((r) => r.status === "pending").length },
            { v: "gallery", label: t.admin.gallery || "Galleria" },
            { v: "availability", label: t.admin.availability || "Disponibilità" },
          ].map((opt) => (
            <button
              key={opt.v}
              data-testid={`view-${opt.v}`}
              onClick={() => setView(opt.v)}
              className={`relative px-5 py-3 text-sm tracking-widest uppercase transition border-b-2 ${
                view === opt.v
                  ? "text-[#C85A3C] border-[#C85A3C]"
                  : "text-[#FAF9F6]/60 border-transparent hover:text-[#FAF9F6]"
              }`}
            >
              {opt.label}
              {opt.pending > 0 && view !== opt.v && (
                <span className="ml-2 inline-flex items-center justify-center text-[10px] bg-[#C85A3C] text-[#FAF9F6] rounded-full w-5 h-5">
                  {opt.pending}
                </span>
              )}
            </button>
          ))}
        </div>

        {view === "bookings" ? (
          <>
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-6">
            <h1 className="font-serif-display text-5xl">{t.admin.bookings}</h1>
            <div className="inline-flex border border-[#FAF9F6]/20 rounded-sm overflow-hidden">
              {[
                { v: "list", label: t.admin.listView || "Elenco" },
                { v: "calendar", label: t.admin.calendarView || "Calendario" },
              ].map((m) => (
                <button
                  key={m.v}
                  data-testid={`booking-view-${m.v}`}
                  onClick={() => setBookingViewMode(m.v)}
                  className={`px-4 py-1.5 text-xs tracking-widest uppercase transition ${
                    bookingViewMode === m.v
                      ? "bg-[#C85A3C] text-[#FAF9F6]"
                      : "text-[#FAF9F6]/70 hover:text-[#FAF9F6]"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          {bookingViewMode === "list" && (
          <div className="flex gap-2 flex-wrap">
            {[
              { v: "all", label: `${t.admin.all} (${bookings.length})` },
              { v: "pending", label: t.admin.statuses.pending },
              { v: "confirmed", label: t.admin.statuses.confirmed },
              { v: "cancelled", label: t.admin.statuses.cancelled },
              { v: "abandoned", label: `${t.admin.statuses.abandoned} (${bookings.filter((b) => b.status === "abandoned").length})` },
            ].map((s) => (
              <button
                key={s.v}
                data-testid={`filter-${s.v}`}
                onClick={() => setTab(s.v)}
                className={`px-4 py-2 text-xs tracking-widest uppercase border rounded-sm transition ${
                  tab === s.v
                    ? "bg-[#C85A3C] border-[#C85A3C] text-[#FAF9F6]"
                    : "border-[#FAF9F6]/25 text-[#FAF9F6]/70 hover:border-[#C85A3C]"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          )}
        </div>

        {bookingViewMode === "calendar" ? (
          <BookingsCalendar
            bookings={bookings}
            token={token}
            lang={lang}
            t={t}
            onRefresh={fetchBookings}
          />
        ) : loading ? (
          <p className="text-[#FAF9F6]/60">…</p>
        ) : filtered.length === 0 ? (
          <p className="text-[#FAF9F6]/60 py-20 text-center font-serif-display text-2xl" data-testid="no-bookings">
            {t.admin.noBookings}
          </p>
        ) : (
          <div className="overflow-x-auto border border-[#FAF9F6]/10">
            <table className="w-full text-sm" data-testid="bookings-table">
              <thead className="bg-[#FAF9F6]/5">
                <tr className="text-left">
                  {[t.admin.columns.date, t.admin.columns.slot, t.admin.columns.class, t.admin.columns.guests, t.admin.columns.name, t.admin.columns.contact, t.admin.columns.message, t.admin.columns.received, t.admin.columns.status, t.admin.columns.actions].map((h) => (
                    <th key={h} className="px-4 py-4 overline text-[#FAF9F6]/60 border-b border-[#FAF9F6]/10">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr key={b.id} className="border-b border-[#FAF9F6]/5 hover:bg-[#FAF9F6]/5" data-testid={`booking-row-${b.id}`}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="font-serif-display text-xl text-[#FAF9F6] leading-tight" data-testid={`booking-date-${b.id}`}>
                        {format(parseISO(b.date), "EEEE", { locale })}
                      </div>
                      <div className="text-[#C85A3C] text-sm tracking-wide mt-0.5">
                        {format(parseISO(b.date), "d MMMM yyyy", { locale })}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-[#FAF9F6]/90 font-medium tabular-nums">
                      {b.time_slot === "lunch" ? "13:00" : b.time_slot === "dinner" ? "19:30" : b.time_slot}
                    </td>
                    <td className="px-4 py-4 text-[#FAF9F6]/80">
                      {t.booking[b.class_type] || b.class_type}
                    </td>
                    <td className="px-4 py-4">{b.guests}</td>
                    <td className="px-4 py-4">{b.name}</td>
                    <td className="px-4 py-4 text-[#FAF9F6]/70 text-xs">
                      <div>{b.email}</div>
                      <div>{b.phone}</div>
                    </td>
                    <td className="px-4 py-4 text-[#FAF9F6]/60 text-xs max-w-xs">
                      {b.message || "—"}
                    </td>
                    <td className="px-4 py-4 text-[#FAF9F6]/55 text-xs whitespace-nowrap" data-testid={`received-${b.id}`}>
                      <div>{format(parseISO(b.created_at), "d MMM", { locale })}</div>
                      <div className="text-[#FAF9F6]/40">{format(parseISO(b.created_at), "HH:mm")}</div>
                    </td>
                    <td className="px-4 py-4">
                      <span data-testid={`booking-status-${b.id}`} className={statusBadge(b.status)}>{t.admin.statuses[b.status] || b.status}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        {b.status !== "confirmed" && (
                          <button
                            data-testid={`confirm-${b.id}`}
                            onClick={() => updateStatus(b.id, "confirmed")}
                            className="text-xs px-3 py-1.5 border border-[#6B7A3F] text-[#6B7A3F] hover:bg-[#6B7A3F] hover:text-[#FAF9F6] rounded-sm transition"
                          >
                            {t.admin.confirm}
                          </button>
                        )}
                        {b.status !== "cancelled" && (
                          <button
                            data-testid={`cancel-${b.id}`}
                            onClick={() => updateStatus(b.id, "cancelled")}
                            className="text-xs px-3 py-1.5 border border-[#FAF9F6]/30 text-[#FAF9F6]/70 hover:border-[#C85A3C] hover:text-[#C85A3C] rounded-sm transition"
                          >
                            {t.admin.cancel}
                          </button>
                        )}
                        <button
                          data-testid={`delete-${b.id}`}
                          onClick={() => remove(b.id)}
                          className="text-xs px-3 py-1.5 text-[#FAF9F6]/40 hover:text-[#C85A3C] rounded-sm transition"
                        >
                          {t.admin.delete}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        ) : view === "reviews" ? (
          // ===== Reviews view =====
          <>
            <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
              <h1 className="font-serif-display text-5xl">{t.admin.reviewsTitle}</h1>
              <div className="flex gap-2 flex-wrap items-center">
                <button
                  data-testid="add-review-btn"
                  onClick={() => setShowAddReview(true)}
                  className="px-6 py-2 text-xs tracking-widest uppercase rounded-sm bg-[#C85A3C] text-[#FAF9F6] hover:bg-[#A6472D] transition"
                >
                  {t.admin.addReview || "+ Aggiungi recensione"}
                </button>
                {[
                  { v: "all", label: `${t.admin.all} (${reviews.length})` },
                  { v: "pending", label: t.admin.statuses.pending },
                  { v: "approved", label: t.admin.statuses.approved },
                  { v: "rejected", label: t.admin.statuses.rejected },
                ].map((s) => (
                  <button
                    key={s.v}
                    data-testid={`review-filter-${s.v}`}
                    onClick={() => setTab(s.v)}
                    className={`px-4 py-2 text-xs tracking-widest uppercase border rounded-sm transition ${
                      tab === s.v
                        ? "bg-[#C85A3C] border-[#C85A3C] text-[#FAF9F6]"
                        : "border-[#FAF9F6]/25 text-[#FAF9F6]/70 hover:border-[#C85A3C]"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {showAddReview && (
              <AddReviewForm
                token={token}
                t={t}
                onClose={() => setShowAddReview(false)}
                onCreated={() => { setShowAddReview(false); fetchReviews(); }}
              />
            )}

            {loading ? (
              <p className="text-[#FAF9F6]/60">…</p>
            ) : filtered.length === 0 ? (
              <p className="text-[#FAF9F6]/60 py-20 text-center font-serif-display text-2xl" data-testid="no-reviews">
                {t.admin.noReviews}
              </p>
            ) : (
              <div className="overflow-x-auto border border-[#FAF9F6]/10">
                <table className="w-full text-sm" data-testid="reviews-table">
                  <thead className="bg-[#FAF9F6]/5">
                    <tr className="text-left">
                      {[t.admin.reviewColumns.rating, t.admin.reviewColumns.name, t.admin.reviewColumns.origin, t.admin.reviewColumns.quote, t.admin.reviewColumns.received, t.admin.reviewColumns.status, t.admin.reviewColumns.actions].map((h) => (
                        <th key={h} className="px-4 py-4 overline text-[#FAF9F6]/60 border-b border-[#FAF9F6]/10">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.id} className="border-b border-[#FAF9F6]/5 hover:bg-[#FAF9F6]/5 align-top" data-testid={`review-row-${r.id}`}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex gap-0.5">
                            {[1,2,3,4,5].map((n) => (
                              <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-[#C85A3C] text-[#C85A3C]" : "text-[#FAF9F6]/20"}`} strokeWidth={1.5}/>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-4 font-medium">{r.name}</td>
                        <td className="px-4 py-4 text-[#FAF9F6]/70">{r.origin}</td>
                        <td className="px-4 py-4 max-w-md text-[#FAF9F6]/80 font-serif-display text-base italic leading-snug">"{r.quote}"</td>
                        <td className="px-4 py-4 text-[#FAF9F6]/55 text-xs whitespace-nowrap">
                          <div>{format(parseISO(r.created_at), "d MMM", { locale })}</div>
                          <div className="text-[#FAF9F6]/40">{format(parseISO(r.created_at), "HH:mm")}</div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={statusBadge(r.status)}>{t.admin.statuses[r.status]}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex gap-2 flex-wrap">
                            {r.status !== "approved" && (
                              <button
                                data-testid={`approve-${r.id}`}
                                onClick={() => updateReviewStatus(r.id, "approved")}
                                className="text-xs px-3 py-1.5 border border-[#6B7A3F] text-[#6B7A3F] hover:bg-[#6B7A3F] hover:text-[#FAF9F6] rounded-sm transition"
                              >
                                {t.admin.approve}
                              </button>
                            )}
                            {r.status !== "rejected" && (
                              <button
                                data-testid={`reject-${r.id}`}
                                onClick={() => updateReviewStatus(r.id, "rejected")}
                                className="text-xs px-3 py-1.5 border border-[#FAF9F6]/30 text-[#FAF9F6]/70 hover:border-[#C85A3C] hover:text-[#C85A3C] rounded-sm transition"
                              >
                                {t.admin.reject}
                              </button>
                            )}
                            <button
                              data-testid={`review-delete-${r.id}`}
                              onClick={() => removeReview(r.id)}
                              className="text-xs px-3 py-1.5 text-[#FAF9F6]/40 hover:text-[#C85A3C] rounded-sm transition"
                            >
                              {t.admin.delete}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : view === "gallery" ? (
          <GalleryTab token={token} t={t} />
        ) : (
          <AvailabilityTab token={token} t={t} />
        )}
      </main>
    </div>
  );
}

function AddReviewForm({ token, t, onClose, onCreated }) {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [form, setForm] = useState({ name: "", origin: "", rating: 5, quote: "", language: "en" });
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.origin.trim() || !form.quote.trim() || form.quote.trim().length < 10) {
      toast.error(t.admin.addReviewValidation || "Compila nome, città e testo (min 10 caratteri).");
      return;
    }
    setBusy(true);
    try {
      const created = await axios.post(`${API}/reviews`, form);
      const id = created.data.id;
      await axios.patch(
        `${API}/admin/reviews/${id}/status`,
        { status: "approved" },
        { headers: { "X-Admin-Password": token } }
      );
      toast.success(t.admin.addReviewSuccess || "Recensione pubblicata");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      data-testid="add-review-form"
      onSubmit={submit}
      className="bg-[#FAF9F6]/5 border border-[#FAF9F6]/15 p-6 md:p-8 rounded-sm mb-8"
    >
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="text-xs tracking-widest uppercase text-[#FAF9F6]/60">{t.admin.reviewColumns.name}</label>
          <input
            data-testid="add-review-name"
            value={form.name}
            onChange={(e) => upd("name", e.target.value)}
            className="w-full mt-2 bg-transparent border-b border-[#FAF9F6]/30 py-2 focus:outline-none focus:border-[#C85A3C]"
            placeholder="Caroline Duke"
          />
        </div>
        <div>
          <label className="text-xs tracking-widest uppercase text-[#FAF9F6]/60">{t.admin.reviewColumns.origin}</label>
          <input
            data-testid="add-review-origin"
            value={form.origin}
            onChange={(e) => upd("origin", e.target.value)}
            className="w-full mt-2 bg-transparent border-b border-[#FAF9F6]/30 py-2 focus:outline-none focus:border-[#C85A3C]"
            placeholder="United States"
          />
        </div>
        <div>
          <label className="text-xs tracking-widest uppercase text-[#FAF9F6]/60">{t.admin.reviewColumns.rating}</label>
          <div className="flex gap-1 mt-2" data-testid="add-review-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`add-review-star-${n}`}
                onClick={() => upd("rating", n)}
                className={`text-2xl transition ${n <= form.rating ? "text-[#C85A3C]" : "text-[#FAF9F6]/25 hover:text-[#FAF9F6]/50"}`}
              >
                ★
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs tracking-widest uppercase text-[#FAF9F6]/60">{t.admin.reviewLanguage || "Lingua"}</label>
          <select
            data-testid="add-review-language"
            value={form.language}
            onChange={(e) => upd("language", e.target.value)}
            className="w-full mt-2 bg-transparent border-b border-[#FAF9F6]/30 py-2 focus:outline-none focus:border-[#C85A3C]"
          >
            {[["it","Italiano"],["en","English"],["fr","Français"],["es","Español"],["de","Deutsch"],["ja","日本語"]].map(([v, l]) => (
              <option key={v} value={v} className="bg-[#1F1A14]">{l}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs tracking-widest uppercase text-[#FAF9F6]/60">{t.admin.reviewColumns.quote}</label>
          <textarea
            data-testid="add-review-quote"
            value={form.quote}
            onChange={(e) => upd("quote", e.target.value)}
            rows={4}
            className="w-full mt-2 bg-transparent border border-[#FAF9F6]/30 p-3 rounded-sm focus:outline-none focus:border-[#C85A3C] leading-relaxed"
            placeholder="Testo della recensione…"
          />
        </div>
      </div>
      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2.5 text-xs tracking-widest uppercase border border-[#FAF9F6]/30 rounded-sm hover:border-[#C85A3C]"
        >
          {t.admin.cancel || "Annulla"}
        </button>
        <button
          type="submit"
          disabled={busy}
          data-testid="add-review-submit"
          className="px-6 py-2.5 text-xs tracking-widest uppercase rounded-sm bg-[#C85A3C] text-[#FAF9F6] hover:bg-[#A6472D] disabled:opacity-50"
        >
          {busy ? "…" : (t.admin.publish || "Pubblica")}
        </button>
      </div>
    </form>
  );
}


function GalleryTab({ token, t }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/gallery`);
      setPhotos(r.data || []);
    } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          await axios.post(`${API}/gallery/upload`, fd, {
            headers: { "X-Admin-Password": token, "Content-Type": "multipart/form-data" },
          });
          toast.success(`${file.name} caricata`);
        } catch (err) {
          toast.error(`Errore su ${file.name}: ${err?.response?.data?.detail || err.message}`);
        }
      }
      await load();
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t.admin.confirmDelete || "Sei sicuro?")) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, { headers: { "X-Admin-Password": token } });
      toast.success("Foto eliminata");
      setPhotos((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <h1 className="font-serif-display text-5xl">{t.admin.gallery || "Galleria"}</h1>
        <label
          data-testid="gallery-upload-btn"
          className={`px-6 py-3 text-xs tracking-widest uppercase rounded-sm cursor-pointer transition ${
            uploading
              ? "bg-[#FAF9F6]/10 text-[#FAF9F6]/50"
              : "bg-[#C85A3C] text-[#FAF9F6] hover:bg-[#A6472D]"
          }`}
        >
          {uploading ? "Caricamento…" : (t.admin.uploadPhotos || "Carica foto")}
          <input
            data-testid="gallery-upload-input"
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            disabled={uploading}
            onChange={onUpload}
          />
        </label>
      </div>

      {loading ? (
        <p className="text-[#FAF9F6]/60">…</p>
      ) : photos.length === 0 ? (
        <p className="text-[#FAF9F6]/60 py-20 text-center font-serif-display text-2xl">
          {t.admin.noPhotos || "Nessuna foto ancora."}
        </p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-admin-grid">
          {photos.map((p) => {
            const src = p.url.startsWith("http") ? p.url : `${process.env.REACT_APP_BACKEND_URL}${p.url}`;
            return (
              <div
                key={p.id}
                data-testid={`gallery-photo-${p.id}`}
                className={`relative group bg-[#FAF9F6]/5 overflow-hidden border border-[#FAF9F6]/10 ${
                  p.aspect === "portrait" ? "aspect-[3/4]" : "aspect-[4/3]"
                }`}
              >
                <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
                <button
                  data-testid={`gallery-delete-${p.id}`}
                  onClick={() => onDelete(p.id)}
                  className="absolute top-2 right-2 bg-[#1F1A14]/85 hover:bg-[#C85A3C] text-[#FAF9F6] w-9 h-9 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                  title={t.admin.delete}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
